import { yupResolver } from '@hookform/resolvers/yup';
import { LoadingButton } from '@mui/lab';
import { Alert, IconButton, InputAdornment, Link, Stack } from '@mui/material';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import * as Yup from 'yup';

import { useAuthContext } from '../../auth/useAuthContext';
import FormProvider, { RHFTextField } from '../../components/hook-form';
import Iconify from '../../components/iconify';
import { useLocales } from '../../locales';

// ----------------------------------------------------------------------

type FormValuesProps = {
  afterSubmit?: string;
  email: string;
  password: string;
};

const AuthLoginForm = () => {
  const { login } = useAuthContext();
  const { translate } = useLocales();

  const [showPassword, setShowPassword] = useState(false);

  const LoginSchema = Yup.object().shape({
    email: Yup.string()
      .required('Email is required')
      .email('Email must be a valid email address'),
    password: Yup.string().required('Password is required'),
  });

  const defaultValues = {
    email: '',
    password: '',
  };

  const methods = useForm<FormValuesProps>({
    defaultValues,
    resolver: yupResolver(LoginSchema),
  });

  const {
    formState: { errors, isSubmitSuccessful, isSubmitting },
    handleSubmit,
    setError,
  } = methods;

  const onSubmit = async (data: FormValuesProps) => {
    try {
      await login(data.email.toLowerCase(), data.password);
    } catch (error) {
      console.error(error);

      setError('afterSubmit', {
        ...error,
        message: error.detail,
      });
    }
  };

  return (
    <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
      <Stack spacing={3}>
        {!!errors.afterSubmit && (
          <Alert severity="error">{errors.afterSubmit.message}</Alert>
        )}

        <RHFTextField label={`${translate('email_address')}`} name="email" />

        <RHFTextField
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  edge="end"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  <Iconify
                    icon={showPassword ? 'eva:eye-fill' : 'eva:eye-off-fill'}
                  />
                </IconButton>
              </InputAdornment>
            ),
          }}
          label={`${translate('password')}`}
          name="password"
          type={showPassword ? 'text' : 'password'}
        />
      </Stack>

      <Stack alignItems="flex-end" sx={{ my: 2 }}>
        <Link
          color="inherit"
          href="/forgot-password"
          underline="always"
          variant="body2"
        >
          {`${translate('forgot_password')}`}
        </Link>
      </Stack>

      <LoadingButton
        color="primary"
        fullWidth
        loading={isSubmitSuccessful || isSubmitting}
        size="large"
        type="submit"
        variant="contained"
      >
        {`${translate('login')}`}
      </LoadingButton>
    </FormProvider>
  );
};

export default AuthLoginForm;
